import { c as _c } from "react/compiler-runtime";
import { Checkbox, Form, InlineAlert } from '@onefootprint/ui';
import { type Ref, useImperativeHandle } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useSession from '@/hooks/use-session';
import { onModEnter } from '@/playbook-builder/utils/on-mod-enter';
type FormData = {
  versionDescription: string;
  skipValidation: boolean;
};
type VersionFormProps = {
  ref?: Ref<VersionFormHandle>;
  action: 'draft' | 'deploy' | 'publish';
  hideAlert?: boolean;
  isProduction?: boolean;
  graphHasErrors: boolean;
  /** `isSubmitting`, which guards Cmd+Enter, stays true until this resolves. */
  onSubmit: (formData: FormData) => Promise<void>;
};
const VersionForm = t0 => {
  const $ = _c(44);
  const {
    ref,
    action,
    hideAlert: t1,
    isProduction: t2,
    graphHasErrors,
    onSubmit
  } = t0;
  const hideAlert = t1 === undefined ? false : t1;
  const isProduction = t2 === undefined ? false : t2;
  const {
    t
  } = useTranslation("playbook-builder");
  const {
    data: t3
  } = useSession();
  const {
    user
  } = t3;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      defaultValues: {
        versionDescription: "",
        skipValidation: false
      }
    };
    $[0] = t4;
  } else {
    t4 = $[0];
  }
  const formMethods = useForm(t4);
  const {
    register,
    handleSubmit,
    setFocus,
    formState: t5
  } = formMethods;
  const {
    errors,
    isSubmitting
  } = t5;
  const submit = handleSubmit(onSubmit);
  let t6;
  if ($[1] !== setFocus) {
    t6 = () => ({
      focus: () => setFocus("versionDescription")
    });
    $[1] = setFocus;
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  useImperativeHandle(ref, t6);
  let t7;
  if ($[3] !== action || $[4] !== isProduction) {
    t7 = () => {
      if (action === "draft") {
        return "info";
      }
      if (action === "deploy") {
        return "warning";
      }
      if (isProduction) {
        return "error";
      }
      return "warning";
    };
    $[3] = action;
    $[4] = isProduction;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  const getAlertVariant = t7;
  let t8;
  if ($[6] !== action || $[7] !== isProduction || $[8] !== t) {
    t8 = () => {
      if (action === "draft") {
        return t(_temp);
      }
      if (action === "deploy") {
        return t(_temp2);
      }
      if (isProduction) {
        return t(_temp3);
      }
      return t(_temp4);
    };
    $[6] = action;
    $[7] = isProduction;
    $[8] = t;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const getAlertText = t8;
  const T0 = FormProvider;
  const t9 = "version-confirmation-form";
  const t10 = onModEnter(() => {
    if (!isSubmitting) {
      submit();
    }
  });
  let t11;
  if ($[10] !== getAlertText || $[11] !== getAlertVariant || $[12] !== hideAlert) {
    t11 = !hideAlert && <InlineAlert variant={getAlertVariant()}>{getAlertText()}</InlineAlert>;
    $[10] = getAlertText;
    $[11] = getAlertVariant;
    $[12] = hideAlert;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== t) {
    t12 = t(_temp5);
    $[14] = t;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] !== t) {
    t13 = t(_temp6);
    $[16] = t;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  if ($[18] !== t) {
    t14 = t(_temp7);
    $[18] = t;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] !== t14) {
    t15 = {
      required: t14
    };
    $[20] = t14;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  let t16;
  if ($[22] !== t12 || $[23] !== t13 || $[24] !== t15) {
    t16 = <Form.Textarea size="compact" name="versionDescription" label={t12} placeholder={t13} rules={t15} rows={3} />;
    $[22] = t12;
    $[23] = t13;
    $[24] = t15;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] !== t11 || $[27] !== t16) {
    t17 = <div className="flex flex-col gap-4">{t11}{t16}</div>;
    $[26] = t11;
    $[27] = t16;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  if ($[29] !== errors || $[30] !== graphHasErrors || $[31] !== register || $[32] !== user?.isCxOpsEmployee || $[33] !== user?.isRiskOpsEmployee) {
    t18 = graphHasErrors && (user?.isRiskOpsEmployee || user?.isCxOpsEmployee) && <Checkbox label="Save graph with errors (Employees only)" hasError={!!errors.skipValidation} hint={errors.skipValidation?.message} {...register("skipValidation", {
      required: "This graph has errors. You must select this option to save."
    })} />;
    $[29] = errors;
    $[30] = graphHasErrors;
    $[31] = register;
    $[32] = user?.isCxOpsEmployee;
    $[33] = user?.isRiskOpsEmployee;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== submit || $[36] !== t10 || $[37] !== t17 || $[38] !== t18) {
    t19 = <form id={t9} onSubmit={submit} onKeyDownCapture={t10}>{t17}{t18}</form>;
    $[35] = submit;
    $[36] = t10;
    $[37] = t17;
    $[38] = t18;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== T0 || $[41] !== formMethods || $[42] !== t19) {
    t20 = <T0 {...formMethods}>{t19}</T0>;
    $[40] = T0;
    $[41] = formMethods;
    $[42] = t19;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  return t20;
};
export type VersionFormHandle = {
  focus: () => void;
};
export default VersionForm;
function _temp($) {
  return $.versionDialog.draftAlert;
}
function _temp2($_0) {
  return $_0.versionDialog.deployAlert;
}
function _temp3($_1) {
  return $_1.versionDialog.publishProdAlert;
}
function _temp4($_2) {
  return $_2.versionDialog.publishAlert;
}
function _temp5($_3) {
  return $_3.versionDialog.description;
}
function _temp6($_4) {
  return $_4.versionDialog.versionDescriptionPlaceholder;
}
function _temp7($_5) {
  return $_5.versionDialog.versionDescriptionRequired;
}